import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPublicKey } from "../api";
import { getInitials, getAvatarColor } from "./userListUtils";
import { jwtDecode } from "jwt-decode";
import { SocketContext } from "../context/SocketContext";
import { initializeWebSocket } from "../ChatPage/chatUtils";

export default function Profile() {
  const { mobile } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);

  const socketRef = useContext(SocketContext);
  const sharedKeyRef = useRef(null);

  const token = localStorage.getItem("token");
  let myMobile = null;
  try {
    myMobile = token ? jwtDecode(token).mobile : null;
  } catch (e) {
    myMobile = null;
  }

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await getPublicKey(mobile);
        setUser(res.data);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        // If API fails, create a basic user object with mobile
        setUser({ mobile, name: "Unknown", avatar: null });
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [mobile, navigate]);

  useEffect(() => {
    if (!myMobile || !token) return;

    // Initialize WebSocket for call signaling
    const onSignal = () => {}; // No-op for profile page
    initializeWebSocket(
      token,
      socketRef,
      sharedKeyRef,
      myMobile,
      mobile,
      setIsConnected,
      () => {}, // No messages handler
      onSignal
    );
  }, [token, myMobile, mobile]);

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3">Loading profile...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mt-5 text-center">
        <p>User not found.</p>
        <button className="btn btn-primary" onClick={() => navigate("/users")}>
          Back to Users
        </button>
      </div>
    );
  }

  const initiateCall = (callType) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      alert("Socket not connected.");
      return;
    }

    socketRef.current.send(
      JSON.stringify({
        action: "signal",
        data: JSON.stringify({
          type: "call:offer",
          callType,
          from: myMobile,
          to: mobile,
        }),
      })
    );

    navigate(`/call/${mobile}`, {
      state: { callType, isCaller: true },
    });
  };

  return (
    <div className="container-fluid" style={{ height: '100vh', backgroundColor: '#f0f0f0' }}>
      {/* Header */}
      <div className="d-flex align-items-center p-3 bg-white border-bottom">
        <button
          className="btn btn-link text-secondary me-3 p-0"
          onClick={() => navigate("/users")}
        >
          <i className="bi bi-arrow-left fs-4"></i>
        </button>
        <h5 className="mb-0">Profile</h5>
      </div>

      {/* Profile Content */}
      <div className="d-flex flex-column align-items-center bg-white" style={{ minHeight: 'calc(100vh - 76px)' }}>
        {/* Avatar */}
        <div className="mt-4 mb-3">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt="Avatar"
              className="rounded-circle"
              style={{ width: 150, height: 150, objectFit: "cover" }}
            />
          ) : (
            <div
              className="rounded-circle text-white d-flex justify-content-center align-items-center"
              style={{
                width: 150,
                height: 150,
                fontSize: 60,
                backgroundColor: getAvatarColor(user.name || ""),
              }}
            >
              {getInitials(user.name || "")}
            </div>
          )}
        </div>

        {/* Name */}
        <h4 className="mb-1 text-center">{user.name || "Unknown"}</h4>

        {/* Mobile */}
        <p className="text-muted mb-4">{user.mobile}</p>

        {/* Action Buttons */}
        <div className="d-flex gap-3 w-100 px-3" style={{ maxWidth: 400 }}>
          <button
            className="btn btn-success flex-fill d-flex flex-column align-items-center py-2"
            onClick={() => navigate(`/chat/${user.mobile}`, { state: { recipientName: user.name, recipientAvatar: user.avatar } })}
          >
            <i className="bi bi-chat-dots fs-4 mb-1"></i>
            <span className="small">Message</span>
          </button>
          <button
            className="btn btn-primary flex-fill d-flex flex-column align-items-center py-2"
            onClick={() => initiateCall('video')}
          >
            <i className="bi bi-camera-video fs-4 mb-1"></i>
            <span className="small">Video</span>
          </button>
          <button
            className="btn btn-primary flex-fill d-flex flex-column align-items-center py-2"
            onClick={() => initiateCall('audio')}
          >
            <i className="bi bi-telephone fs-4 mb-1"></i>
            <span className="small">Call</span>
          </button>
        </div>
      </div>
    </div>
  );
}
